import { URL_TYPES } from '@wix/wix-vod-constants/common';
import { PRODUCTION_HOSTS } from '@wix/wix-vod-constants/env';
import { parseChannelUrl, parseVideoUrl } from './utils/parse-url';
class YoutubeService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.MAX_IDS_IN_REQUEST = 50;
        this.EMPTY_PAGE_BUDGET = 20;
        this.channelIdToPlaylistIdMap = new Map();
    }
    async get(url, requestOptions) {
        const params = typeof window !== 'undefined' &&
            window.hasOwnProperty('BusinessManagerAPI')
            ? {
                artifact: 'wix-vod-library',
            }
            : undefined;
        return this.httpClient.get(PRODUCTION_HOSTS.pixWix + url, {
            params: {
                ...params,
                ...requestOptions?.params,
            },
            ...requestOptions,
        });
    }
    fetchChannels(options, requestConfig) {
        options = {
            order: 'relevance',
            ids: [],
            maxResults: this.MAX_IDS_IN_REQUEST,
            shouldThrowWhenNoItems: false,
            ...options,
        };
        const params = {
            pageToken: options.cursor,
            order: options.order,
            forUsername: options.userName,
            // send all ids in '&id=1,2,3" format
            id: options.ids && options.ids.length ? options.ids.join(',') : null,
            maxResults: options.maxResults,
            shouldThrowWhenNoItems: options.shouldThrowWhenNoItems,
        };
        return this.get('/youtube/channels', {
            params,
            ...requestConfig,
        });
    }
    fetchPlayLists(options = {}, requestConfig) {
        const params = {
            shouldThrowWhenNoItems: options.shouldThrowWhenNoItems || false,
            id: options.ids && options.ids.length ? options.ids.join(',') : null,
        };
        return this.get('/youtube/playlists', {
            params,
            ...requestConfig,
        });
    }
    makeChunks(ids, size) {
        return ids.reduce((acc, id) => {
            const lastChunkIndex = acc.length - 1;
            if (acc[lastChunkIndex].length === size) {
                acc.push([id]);
            }
            else {
                acc[lastChunkIndex].push(id);
            }
            return acc;
        }, [[]]);
    }
    batchFetch(ids, fetcher) {
        const chunks = this.makeChunks(ids, this.MAX_IDS_IN_REQUEST);
        const requests = chunks.map((channelIds) => fetcher(channelIds));
        return Promise.all(requests);
    }
    parseChannelUrl(url) {
        return parseChannelUrl(url);
    }
    parseVideoUrl(url) {
        return parseVideoUrl(url);
    }
    getChannelsByIds(ids, requestConfig) {
        return this.batchFetch(ids, (chunk) => this.fetchChannels({
            ids: chunk,
        }, requestConfig)).then((responses) => {
            return {
                ...responses[0],
                data: {
                    items: responses.reduce((acc, response) => response.data && response.data.items
                        ? acc.concat(response.data.items)
                        : acc, []),
                },
            };
        });
    }
    getChannelById(id, requestConfig) {
        return this.getChannelsByIds([id], requestConfig).then((response) => {
            return {
                ...response,
                data: response.data.items[0],
            };
        });
    }
    getChannelsByUsername(userName, requestConfig) {
        return this.fetchChannels({ userName }, requestConfig);
    }
    getPlayListsByIds(ids, requestConfig) {
        // TODO: add shouldThrowWhenNoItems to parameters
        return this.batchFetch(ids, (chunk) => this.fetchPlayLists({ ids: chunk }, requestConfig)).then((responses) => {
            return {
                ...responses[0],
                data: {
                    items: responses.reduce((acc, response) => response.data && response.data.items
                        ? acc.concat(response.data.items)
                        : acc, []),
                },
            };
        });
    }
    getPlaylistById(id, requestConfig) {
        return this.getPlayListsByIds([id], requestConfig).then((response) => {
            return {
                ...response,
                data: response.data.items[0],
            };
        });
    }
    getPlaylistIdByChannelId(channelId, requestConfig) {
        const cachedPlaylistId = this.channelIdToPlaylistIdMap.get(channelId);
        if (cachedPlaylistId) {
            return Promise.resolve(cachedPlaylistId);
        }
        return this.getChannelById(channelId, requestConfig).then(({ data }) => {
            const dta = data;
            const playlistId = dta.contentDetails.relatedPlaylists.uploads;
            this.channelIdToPlaylistIdMap.set(channelId, playlistId);
            return playlistId;
        });
    }
    fetchVideos(videoIds, requestConfig) {
        const requestOptions = {
            params: {
                id: videoIds.join(','),
            },
            ...requestConfig,
        };
        return this.get('/youtube/videos', requestOptions);
    }
    getVideosByIds(videoIds, requestConfig) {
        return this.batchFetch(videoIds, (idsChunk) => this.fetchVideos(idsChunk, requestConfig)).then((responses) => {
            return {
                ...responses[0],
                data: {
                    items: responses.reduce((acc, response) => acc.concat(response.data.items), []),
                },
            };
        });
    }
    doFetchPlaylistItems(playlistId, maxResults, pageToken, requestConfig) {
        return this.get('/youtube/playlistitems', {
            params: {
                maxResults,
                pageToken,
                playlistId,
            },
            ...requestConfig,
        });
    }
    async fetchChannelVideosByPlaylistId({ playlistId, itemsCount, pageToken = '' }, requestConfig) {
        let items = [];
        let perPage = Math.min(itemsCount, this.MAX_IDS_IN_REQUEST);
        let lastResponse;
        const requestedPageTokens = new Set();
        // A page must either yield items or move the cursor forward. Neither is
        // guaranteed: the upstream can answer 200 with zero items and a *fresh*
        // cursor, which passes the repeated-cursor check below while making no
        // progress at all. Bound the number of pages one call may fetch: enough to
        // satisfy itemsCount, plus slack for legitimately empty pages (private and
        // deleted playlist entries are counted by totalResults but omitted from
        // items, so a short or empty page mid-list is normal and must not be
        // treated as the end of the playlist).
        const maxPages = Math.ceil(itemsCount / this.MAX_IDS_IN_REQUEST) + this.EMPTY_PAGE_BUDGET;
        let pagesFetched = 0;
        const recalculate = (response) => {
            items = items.concat(response.data.items);
            itemsCount = Math.max(itemsCount - response.data.items.length, 0);
            pageToken = response.data.nextPageToken;
            perPage = Math.min(itemsCount, this.MAX_IDS_IN_REQUEST);
        };
        const fetchNextPage = async () => {
            if (pageToken) {
                requestedPageTokens.add(pageToken);
            }
            pagesFetched += 1;
            const response = await this.doFetchPlaylistItems(playlistId, perPage, pageToken, requestConfig);
            recalculate(response);
            // A cursor must move forward. Pix can return the same nextPageToken
            // (or a cycle of previously returned tokens), which otherwise makes
            // this loop issue requests forever.
            if (pageToken && requestedPageTokens.has(pageToken)) {
                pageToken = '';
                response.data.nextPageToken = '';
            }
            return response;
        };
        lastResponse = await fetchNextPage();
        while (itemsCount && pageToken && pagesFetched < maxPages) {
            lastResponse = await fetchNextPage();
        }
        lastResponse.data.items = items;
        return lastResponse;
    }
    async getPlaylistItems({ externalId, urlType, pageToken = '', itemsCount, }, requestConfig) {
        if (urlType === URL_TYPES.CHANNEL) {
            externalId = await this.getPlaylistIdByChannelId(externalId, requestConfig);
        }
        return this.fetchChannelVideosByPlaylistId({
            playlistId: externalId,
            itemsCount,
            pageToken: pageToken ?? undefined,
        }, requestConfig);
    }
    getCategoryList(categoryIds, requestConfig) {
        const ids = categoryIds.join(',');
        return this.get('/youtube/categories', {
            params: {
                ids,
            },
            ...requestConfig,
        });
    }
    async getVideoById(videoId, requestConfig) {
        const response = await this.fetchVideos([videoId], requestConfig);
        return {
            ...response,
            data: {
                ...response.data,
                categoryItems: await this.fetchCategoryItemsForVideos(response.data.items, requestConfig),
            },
        };
    }
    async fetchCategoryItemsForVideos(items, requestConfig) {
        const categoryIds = items.map((item) => item.snippet.categoryId);
        const uniqueCategoryIds = Array.from(new Set(categoryIds));
        try {
            const { data } = await this.getCategoryList(uniqueCategoryIds, requestConfig);
            return data.items;
        }
        catch (e) {
            return [];
        }
    }
    async fetchChannelItemsForVideos(items, requestConfig) {
        const channelIds = items.map((item) => item.snippet.channelId);
        try {
            const { data } = await this.getChannelsByIds(channelIds, requestConfig);
            return data.items;
        }
        catch (e) {
            return [];
        }
    }
    async getChannelVideos({ externalId, urlType, pageToken = '', itemsCount, }, requestConfig) {
        const response = await this.getPlaylistItems({ externalId, urlType, pageToken, itemsCount }, requestConfig);
        const playlistItems = response.data.items;
        const videoIds = playlistItems.map((item) => item.contentDetails.videoId);
        const videosResponse = await this.getVideosByIds(videoIds, requestConfig);
        const videoItems = videosResponse.data.items;
        const [categoryItems, channelItems] = await Promise.all([
            this.fetchCategoryItemsForVideos(videoItems, requestConfig),
            this.fetchChannelItemsForVideos(videoItems, requestConfig),
        ]);
        return {
            ...response,
            data: {
                playlistItems,
                videoItems,
                channelItems,
                categoryItems,
                nextPageToken: response.data.nextPageToken,
                pageInfo: response.data.pageInfo,
            },
        };
    }
}
export { YoutubeService };
//# sourceMappingURL=youtube.js.map